import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useAgRequest,
  useClearAgCommands,
  useClearAgNobles,
  useCreateAgPlan,
  useDeleteAgPlan,
  useImportAgCommands,
  useImportAgNobles,
  useImportAgNoblesFromRequest,
  useNobleExport,
  useRenameAgPlan,
} from '../api/queries';
import type { AgImportResult, AgPlanDetail, AgPlanSummary } from '../api/types';
import { useToast } from '../components/Toast';
import { NOBLES_PER_TARGET } from './mapUtils';

interface Props {
  welt: string;
  plans: AgPlanSummary[];
  plansLoading: boolean;
  planId: number | null;
  onSelectPlan: (id: number | null) => void;
  detail: AgPlanDetail | null;
  detailLoading: boolean;
  hiddenTypes: ReadonlySet<string>;
  onToggleType: (key: string) => void;
  onSetAllTypes: (visible: boolean) => void;
  /** Wirksame Farbe je Typ (lokale Auswahl vor Serverstand). */
  colors: Readonly<Record<string, string>>;
  onChangeColor: (key: string, color: string) => void;
  showNobles: boolean;
  onToggleNobles: () => void;
  /** Eingelesene AGs samt Dorfname/Besitzer (aus den Kartendaten aufgelöst), größte Anzahl zuerst. */
  nobleList: ReadonlyArray<{ x: number; y: number; count: number; planned: number; name: string; owner: string | null }>;
  onFocusNoble: (x: number, y: number) => void;
  /** Zieldörfer, auf die mindestens NOBLES_PER_TARGET AGs verplant sind. */
  completeTargetCount: number;
  /** Zieldörfer, denen noch AGs fehlen, und die Summe der fehlenden AGs. */
  openTargetCount: number;
  missingNobles: number;
}

/**
 * Plan-Bereich der AG-Planer-Seitenleiste: Plan wählen/anlegen, Workbench-
 * Befehle importieren und je erkanntem Angriffstyp Sichtbarkeit (Checkbox)
 * und Farbe (Colorpicker) steuern.
 */
export function PlanPanel({
  welt,
  plans,
  plansLoading,
  planId,
  onSelectPlan,
  detail,
  detailLoading,
  hiddenTypes,
  onToggleType,
  onSetAllTypes,
  colors,
  onChangeColor,
  showNobles,
  onToggleNobles,
  nobleList,
  onFocusNoble,
  completeTargetCount,
  openTargetCount,
  missingNobles,
}: Props) {
  const toast = useToast();
  const create = useCreateAgPlan(welt);
  const rename = useRenameAgPlan(welt);
  const remove = useDeleteAgPlan(welt);
  const clear = useClearAgCommands(welt);
  const clearNobles = useClearAgNobles(welt);
  const importCommands = useImportAgCommands(welt);
  const importNobles = useImportAgNobles(welt);
  const [dialog, setDialog] = useState<'commands' | 'nobles' | 'request' | 'export' | null>(null);

  const selected = plans.find((plan) => plan.id === planId) ?? null;
  // Ungeplant = eingelesene AGs minus die in diesem Plan bereits verplanten (je Dorf nicht unter 0).
  const totalNobles = detail?.nobles.reduce((sum, n) => sum + n[2], 0) ?? 0;
  const plannedNobles = nobleList.reduce((sum, n) => sum + Math.min(n.planned, n.count), 0);
  const unplannedNobles = totalNobles - plannedNobles;

  const handleCreate = () => {
    const name = window.prompt('Name des neuen Plans:');
    if (!name?.trim()) return;
    create.mutate(
      { name },
      {
        onSuccess: ({ plan }) => {
          onSelectPlan(plan.id);
          toast.push('success', 'Plan wurde erstellt.');
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  const handleRename = () => {
    if (!selected) return;
    const name = window.prompt('Neuer Name des Plans:', selected.name);
    if (!name?.trim() || name.trim() === selected.name) return;
    rename.mutate(
      { id: selected.id, name },
      { onSuccess: () => toast.push('success', 'Plan wurde umbenannt.'), onError: (error) => toast.push('error', error.message) },
    );
  };

  const handleDelete = () => {
    if (!selected) return;
    if (!window.confirm(`Plan "${selected.name}" mit allen ${selected.commandCount} Befehlen löschen?`)) return;
    remove.mutate(
      { id: selected.id },
      {
        onSuccess: () => {
          onSelectPlan(null);
          toast.push('success', 'Plan wurde gelöscht.');
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  const handleClear = () => {
    if (!selected || !window.confirm(`Alle Befehle aus "${selected.name}" entfernen?`)) return;
    clear.mutate(
      { id: selected.id },
      { onSuccess: () => toast.push('success', 'Befehle wurden entfernt.'), onError: (error) => toast.push('error', error.message) },
    );
  };

  return (
    <div className="ag-side-section">
      <h3>Plan</h3>
      <select
        value={planId ?? ''}
        onChange={(event) => onSelectPlan(event.target.value ? Number(event.target.value) : null)}
        aria-label="Plan wählen"
        disabled={plansLoading}
      >
        <option value="">– kein Plan –</option>
        {plans.map((plan) => (
          <option key={plan.id} value={plan.id}>
            {plan.name} ({plan.commandCount})
          </option>
        ))}
      </select>
      <div className="button-row">
        <button type="button" className="btn btn-sm" onClick={handleCreate} disabled={create.isPending}>
          Neuer Plan
        </button>
        {selected && (
          <>
            <button type="button" className="btn btn-sm" onClick={() => setDialog('commands')}>
              Befehle importieren
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setDialog('nobles')}>
              AGs einlesen
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setDialog('request')}>
              AG-Abfrage übernehmen
            </button>
            <button type="button" className="btn btn-sm" onClick={handleRename}>
              Umbenennen
            </button>
            <button type="button" className="btn btn-sm" onClick={handleClear} disabled={selected.commandCount === 0}>
              Leeren
            </button>
            <button type="button" className="btn btn-danger btn-sm" onClick={handleDelete}>
              Löschen
            </button>
          </>
        )}
      </div>

      {selected && detailLoading && <p className="muted">Plan wird geladen …</p>}
      {selected && detail && detail.commands.length === 0 && (
        <p className="muted">Noch keine Befehle – über „Befehle importieren" einen Workbench-Export einfügen.</p>
      )}

      {detail && detail.types.length > 0 && (
        <>
          <div className="ag-type-head">
            <span className="muted">
              {detail.commands.length.toLocaleString('de-DE')} Befehle
              {detail.plan.createdByName ? ` · von ${detail.plan.createdByName}` : ''}
            </span>
            <span className="ag-type-all">
              <button type="button" onClick={() => onSetAllTypes(true)}>
                alle
              </button>
              {' / '}
              <button type="button" onClick={() => onSetAllTypes(false)}>
                keine
              </button>
            </span>
          </div>
          <ul className="ag-types">
            {detail.types.map((type) => (
              <li key={type.key}>
                <label className="checkbox-field">
                  <input type="checkbox" checked={!hiddenTypes.has(type.key)} onChange={() => onToggleType(type.key)} />
                  <span className="ag-type-label" title={type.label}>
                    {type.label}
                  </span>
                </label>
                <span className="ag-type-count">{type.count}</span>
                <input
                  type="color"
                  className="ag-color"
                  value={colors[type.key] ?? type.color}
                  onChange={(event) => onChangeColor(type.key, event.target.value)}
                  aria-label={`Farbe für ${type.label}`}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {detail && completeTargetCount + openTargetCount > 0 && (
        <p className="ag-need">
          AG-Bedarf:{' '}
          {openTargetCount > 0 ? (
            <>
              <strong className="ag-need-open">{missingNobles.toLocaleString('de-DE')} AGs fehlen noch</strong> auf {openTargetCount.toLocaleString('de-DE')}{' '}
              {openTargetCount === 1 ? 'Ziel' : 'Zielen'}
            </>
          ) : (
            'alle Ziele versorgt'
          )}
          {completeTargetCount > 0 && (
            <span className="ag-complete">
              {' '}
              · ✓ {completeTargetCount} mit {NOBLES_PER_TARGET} AGs komplett
            </span>
          )}
        </p>
      )}

      {detail && detail.nobles.length > 0 && (
        <div className="ag-noble-section">
          <div className="ag-noble-head">
            <h3>AGs</h3>
            <label className="checkbox-field">
              <input type="checkbox" checked={showNobles} onChange={onToggleNobles} />
              <span>auf Karte</span>
            </label>
          </div>
          <p className="ag-noble-total">
            <strong>{unplannedNobles.toLocaleString('de-DE')}</strong> ungeplant
            <span className="muted">
              {' '}
              · {plannedNobles.toLocaleString('de-DE')} geplant · {totalNobles.toLocaleString('de-DE')} AGs in{' '}
              {detail.nobles.length.toLocaleString('de-DE')} {detail.nobles.length === 1 ? 'Dorf' : 'Dörfern'}
            </span>
          </p>
          <ul className="ag-noble-list">
            {nobleList.map((noble) => (
              <li key={`${noble.x}|${noble.y}`}>
                <button type="button" onClick={() => onFocusNoble(noble.x, noble.y)} title={noble.owner ?? undefined}>
                  <span className="ag-noble-count">{noble.count}</span>
                  <span className="ag-noble-name">
                    {noble.name} ({noble.x}|{noble.y})
                  </span>
                  <span className="muted">{Math.max(0, noble.count - noble.planned)} ungeplant</span>
                </button>
              </li>
            ))}
          </ul>
          {detail.plannedNobles.length > 0 && (
            <button type="button" className="btn btn-sm" onClick={() => setDialog('export')}>
              AG-Befehle exportieren ({detail.plannedNobles.length})
            </button>
          )}
          <button
            type="button"
            className="ag-link-button"
            onClick={() => {
              if (!window.confirm('Alle eingelesenen AGs aus diesem Plan entfernen?')) return;
              clearNobles.mutate(
                { id: detail.plan.id },
                { onSuccess: () => toast.push('success', 'AGs wurden entfernt.'), onError: (error) => toast.push('error', error.message) },
              );
            }}
          >
            alle AGs entfernen
          </button>
        </div>
      )}

      {dialog === 'request' && selected && <RequestImportDialog welt={welt} plan={selected} onClose={() => setDialog(null)} />}
      {dialog === 'export' && selected && <ExportDialog welt={welt} plan={selected} onClose={() => setDialog(null)} />}
      {dialog === 'commands' && selected && (
        <ImportDialog
          title={`Befehle in „${selected.name}" importieren`}
          description={
            'DS-Workbench-„Ankunftsplaner"-Export einfügen (eine Zeile je Befehl). Quell- und Zieldorf werden über ihre Dorf-ID den synchronisierten Weltdaten zugeordnet, der Angriffstyp über das Feld „DS-Workbench-Angriffstyp" der Pakettyp-Vorlagen.'
          }
          placeholder="quellDorfId&zielDorfId&einheit&ankunftMs&typ&false&false&einheiten"
          pending={importCommands.isPending}
          addLabel="Zu bestehenden hinzufügen"
          replaceLabel="Bestehende ersetzen"
          unknownText="Quell- oder Zieldorf ist in den Weltdaten nicht bekannt"
          noun="Befehle"
          onSubmit={(text, mode) => importCommands.mutateAsync({ id: selected.id, text, mode })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'nobles' && selected && (
        <ImportDialog
          title={`AGs in „${selected.name}" einlesen`}
          description={
            'Eine Zeile je Dorf: Koordinate, Leerzeichen, Anzahl der dort stehenden AGs – z. B. „511|432 2" heißt: im Dorf 511|432 stehen 2 AGs. Kommt ein Dorf mehrfach vor, zählt die letzte Zeile.'
          }
          placeholder={'511|432 2\n500|501 1'}
          pending={importNobles.isPending}
          addLabel="Dörfer ergänzen bzw. neu setzen"
          replaceLabel="Gesamten AG-Bestand ersetzen"
          unknownText="Dorf ist in den Weltdaten nicht bekannt"
          noun="Dörfer mit AGs"
          onSubmit={(text, mode) => importNobles.mutateAsync({ id: selected.id, text, mode })}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

interface ImportDialogProps {
  title: string;
  description: string;
  placeholder: string;
  pending: boolean;
  addLabel: string;
  replaceLabel: string;
  unknownText: string;
  noun: string;
  onSubmit: (text: string, mode: 'add' | 'replace') => Promise<AgImportResult>;
  onClose: () => void;
}

/** Eingabedialog für Textimporte (Workbench-Befehle, AGs): Text einfügen, Modus wählen, Ergebnis samt übersprungenen Zeilen sehen. */
function ImportDialog({ title, description, placeholder, pending, addLabel, replaceLabel, unknownText, noun, onSubmit, onClose }: ImportDialogProps) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'add' | 'replace'>('add');
  const [result, setResult] = useState<AgImportResult | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleImport = async () => {
    try {
      const data = await onSubmit(text, mode);
      setResult(data);
      if (data.imported > 0) toast.push('success', `${data.imported} ${noun} übernommen.`);
      if (data.unknownVillageLines.length === 0 && data.invalidLines.length === 0 && data.imported > 0) onClose();
    } catch (error) {
      toast.push('error', error instanceof Error ? error.message : 'Import fehlgeschlagen.');
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box ag-import-box" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="modal-close" aria-label="Schließen" onClick={onClose}>
          ×
        </button>
        <h2 style={{ margin: '0 0 8px' }}>{title}</h2>
        <p className="muted" style={{ margin: '0 0 10px' }}>
          {description}
        </p>
        <textarea rows={10} value={text} onChange={(event) => setText(event.target.value)} placeholder={placeholder} style={{ width: '100%' }} />
        <div className="button-row" style={{ marginTop: 10 }}>
          <label className="checkbox-field">
            <input type="radio" name="ag-import-mode" checked={mode === 'add'} onChange={() => setMode('add')} />
            <span>{addLabel}</span>
          </label>
          <label className="checkbox-field">
            <input type="radio" name="ag-import-mode" checked={mode === 'replace'} onChange={() => setMode('replace')} />
            <span>{replaceLabel}</span>
          </label>
        </div>
        <div className="button-row" style={{ marginTop: 10 }}>
          <button type="button" className="btn btn-primary" disabled={!text.trim() || pending} onClick={() => void handleImport()}>
            {pending ? 'Wird importiert …' : 'Importieren'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Schließen
          </button>
        </div>

        {result && (
          <div style={{ marginTop: 12 }}>
            <p>
              <strong>{result.imported}</strong> {noun} übernommen.
            </p>
            {result.unknownVillageLines.length > 0 && (
              <>
                <p className="muted">
                  {result.unknownVillageLines.length} Zeile(n) übersprungen – {unknownText}:
                </p>
                <textarea rows={3} readOnly value={result.unknownVillageLines.join('\n')} style={{ width: '100%' }} />
              </>
            )}
            {result.invalidLines.length > 0 && (
              <>
                <p className="muted">{result.invalidLines.length} Zeile(n) übersprungen – falsches Format:</p>
                <textarea rows={3} readOnly value={result.invalidLines.join('\n')} style={{ width: '100%' }} />
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Export der verplanten AGs im Workbench-Format: alle Zeilen oder je Absender-Spieler getrennt, jeweils kopierbar. */
function ExportDialog({ welt, plan, onClose }: { welt: string; plan: AgPlanSummary; onClose: () => void }) {
  const toast = useToast();
  const query = useNobleExport(welt, plan.id, true);
  const [grouped, setGrouped] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const copy = async (lines: string[]) => {
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.push('success', 'In die Zwischenablage kopiert.');
    } catch {
      toast.push('error', 'Zwischenablage nicht verfügbar -- bitte manuell markieren und kopieren.');
    }
  };

  const data = query.data;
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box ag-import-box" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="modal-close" aria-label="Schließen" onClick={onClose}>
          ×
        </button>
        <h2 style={{ margin: '0 0 8px' }}>AG-Befehle aus „{plan.name}" exportieren</h2>
        <p className="muted" style={{ margin: '0 0 10px' }}>
          DS-Workbench-„Ankunftsplaner"-Format (Quelldorf-ID, Zieldorf-ID, snob, Ankunft in ms, Typ, …).
        </p>
        {query.isPending && <p className="muted">Wird erstellt …</p>}
        {query.isError && <div className="flash flash-error">{query.error.message}</div>}
        {data && (
          <>
            <div className="button-row">
              <label className="checkbox-field">
                <input type="radio" name="ag-export-mode" checked={!grouped} onChange={() => setGrouped(false)} />
                <span>Alle Befehle</span>
              </label>
              <label className="checkbox-field">
                <input type="radio" name="ag-export-mode" checked={grouped} onChange={() => setGrouped(true)} />
                <span>Je Spieler</span>
              </label>
            </div>
            {data.skipped > 0 && <p className="muted">{data.skipped} Befehl(e) übersprungen – Dorf-ID nicht auflösbar.</p>}
            {(grouped ? data.byPlayer : [{ player: null as string | null, lines: data.lines, all: true }]).map((group) => (
              <div key={'all' in group ? '__alle__' : (group.player ?? '__unbekannt__')} style={{ marginTop: 10 }}>
                {!('all' in group) && (
                  <h4 style={{ margin: '0 0 6px' }}>
                    {group.player ?? 'Unbekannt'} ({group.lines.length} Zeile{group.lines.length === 1 ? '' : 'n'})
                  </h4>
                )}
                <textarea rows={Math.min(10, Math.max(3, group.lines.length + 1))} readOnly value={group.lines.join('\n')} style={{ width: '100%' }} />
                <div className="button-row" style={{ marginTop: 6 }}>
                  <button type="button" className="btn btn-sm" onClick={() => void copy(group.lines)} disabled={group.lines.length === 0}>
                    Kopieren
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Übernimmt den aktuellen Bestand der AG-Abfrage (Angaben der Mitglieder) als AG-Bestand in den Plan. */
function RequestImportDialog({ welt, plan, onClose }: { welt: string; plan: AgPlanSummary; onClose: () => void }) {
  const toast = useToast();
  const overview = useAgRequest(welt);
  const importer = useImportAgNoblesFromRequest(welt);
  const [mode, setMode] = useState<'add' | 'replace'>('replace');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const entries = overview.data?.all ?? [];
  const total = entries.reduce((sum, e) => sum + e.count, 0);
  const members = new Set(entries.map((e) => e.userId)).size;

  const handleImport = () => {
    importer.mutate(
      { id: plan.id, mode },
      {
        onSuccess: (result) => {
          toast.push('success', `${result.imported} Dörfer mit AGs aus der AG-Abfrage übernommen.`);
          onClose();
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="modal-close" aria-label="Schließen" onClick={onClose}>
          ×
        </button>
        <h2 style={{ margin: '0 0 8px' }}>AG-Abfrage in „{plan.name}" übernehmen</h2>
        {overview.isPending && <p className="muted">Wird geladen …</p>}
        {overview.isError && <div className="flash flash-error">{overview.error.message}</div>}
        {overview.data && !overview.data.request && <p className="muted">Es gibt noch keine AG-Anfrage – erstelle sie unter „AG-Abfrage".</p>}
        {overview.data?.request && (
          <>
            <p className="muted" style={{ margin: '0 0 10px' }}>
              Aktuelle Abfrage vom {new Date(overview.data.request.createdAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}:{' '}
              <strong>{total}</strong> AGs in {entries.length} {entries.length === 1 ? 'Dorf' : 'Dörfern'} von {members}{' '}
              {members === 1 ? 'Mitglied' : 'Mitgliedern'}.
            </p>
            <div className="button-row">
              <label className="checkbox-field">
                <input type="radio" name="ag-request-mode" checked={mode === 'replace'} onChange={() => setMode('replace')} />
                <span>Gesamten AG-Bestand des Plans ersetzen</span>
              </label>
              <label className="checkbox-field">
                <input type="radio" name="ag-request-mode" checked={mode === 'add'} onChange={() => setMode('add')} />
                <span>Dörfer ergänzen bzw. neu setzen</span>
              </label>
            </div>
            <div className="button-row" style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-primary" disabled={entries.length === 0 || importer.isPending} onClick={handleImport}>
                {importer.isPending ? 'Wird übernommen …' : 'Übernehmen'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Abbrechen
              </button>
            </div>
            {entries.length === 0 && <p className="muted">Die Abfrage enthält noch keine Angaben.</p>}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
